import { useTranslation } from 'react-i18next';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeSelectableChip } from '@/components/ui/tee-selectable-chip';
import { useAssistantEmptyState } from '@/hooks/useAssistantEmptyState';
import type { AssistantContextTarget } from '@/types/support-assistant';
import { relativeDate } from './AssistantRecentList';

export interface AssistantEmptyStateProps {
  /** undefined / null = no context. */
  context?: AssistantContextTarget | null;
  mobile?: boolean;
  disabled?: boolean;
  onSend: (text: string) => void;
  onSelectSession?: (id: string) => void;
  onViewAll?: () => void;
  onEscalate?: () => void;
}

/** Static suggestion ids used when the server list is unavailable or incomplete. */
const FALLBACK_IDS: Record<string, string[]> = {
  member: ['member.cantBook', 'member.summary', 'member.syncStatus'],
  lead: ['lead.details', 'lead.nextStep', 'lead.summary'],
  location: ['location.integrations', 'location.calendars', 'location.bookingTypes'],
  module: ['module.howToUse', 'module.reportProblem'],
  none: ['none.findMember', 'none.findLead', 'none.bookingFailed'],
};

const MAX_SUGGESTIONS = 3;
const MAX_RECENT = 3;

const TAG_CLASS =
  'shrink-0 rounded-full bg-[var(--color-gold-subtle)] px-2 py-[2px] text-[11px] font-semibold text-[var(--color-gold-on-cream)]';

/** Opening state for the chat body (README C1-C8 and X1). */
export function AssistantEmptyState({
  context,
  mobile = false,
  disabled = false,
  onSend,
  onSelectSession,
  onViewAll,
  onEscalate,
}: AssistantEmptyStateProps) {
  const { t, i18n } = useTranslation();
  const kind = context?.type ?? 'none';
  const { data, isError } = useAssistantEmptyState(context);

  const isModule = context?.type === 'module';
  const label = context?.label ?? '';
  const page = context?.type === 'module' ? context.page : '';
  const moduleName = context?.type === 'module' ? context.module : '';
  const firstWord = label.split(/\s+/)[0] ?? '';
  const localName = kind === 'location' ? label : firstWord;
  const name = !isModule && data?.name ? data.name : localName;

  const heading =
    kind === 'none'
      ? t('assistant.empty.headings.none')
      : isModule
        ? t('assistant.empty.headings.module', { module: moduleName, page })
        : t('assistant.empty.headings.entity', { name });
  const sub = t(`assistant.empty.sub.${kind}`, { name });

  // Server suggestions first (unknown ids skipped), then static fallbacks up to 3.
  const seen = new Set<string>();
  const items: { id: string; text: string }[] = [];
  const push = (id: string, params: Record<string, string>) => {
    if (seen.has(id) || items.length >= MAX_SUGGESTIONS) return;
    const key = `assistant.suggestions.${id}`;
    if (!i18n.exists(key)) return;
    seen.add(id);
    items.push({ id, text: t(key, { name, page, ...params }) });
  };
  if (!isError) for (const s of data?.suggestions ?? []) push(s.id, s.params ?? {});
  for (const id of FALLBACK_IDS[kind] ?? FALLBACK_IDS.none) push(id, {});

  const recent = !isError && data?.hasHistory ? data.recent.slice(0, MAX_RECENT) : [];
  const showRecent = recent.length > 0;
  const showCapabilities = !isError && !!data && !data.hasHistory;
  const showFallbackRow = isModule || kind === 'none';

  const capabilities = [
    ['lookUpTitle', 'lookUpBody'],
    ['evidenceTitle', 'evidenceBody'],
    ['approvalTitle', 'approvalBody'],
  ] as const;

  return (
    <div
      className={`flex flex-1 flex-col gap-6 overflow-y-auto bg-[var(--background-page-body)] py-6 text-left ${mobile ? 'px-4' : 'px-[18px]'}`}
    >
      <div className="flex flex-col gap-2">
        <h2 className="font-[family-name:var(--type-font-family-heading)] text-[20px] font-bold leading-[1.25] text-[var(--text-content-title)]">
          {heading}
        </h2>
        <p className="text-[13px] font-normal leading-[1.55] text-[var(--text-content-sub-title)]">
          {sub}
        </p>
      </div>

      {mobile ? (
        <div className="flex flex-col gap-2">
          {items.map((s) => (
            <TeeButton
              key={s.id}
              variant="secondary"
              size="m"
              className="min-h-[44px] w-full"
              disabled={disabled}
              onClick={() => onSend(s.text)}
              text={s.text}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((s) => (
            <TeeSelectableChip
              key={s.id}
              label={s.text}
              selected={false}
              disabled={disabled}
              onClick={() => onSend(s.text)}
            />
          ))}
        </div>
      )}

      {showFallbackRow && onEscalate && (
        <div className="flex items-center justify-between gap-3 rounded-[8px] border border-dashed border-[var(--border-default)] px-[14px] py-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <p className="text-[13px] font-semibold text-[var(--text-content-title)]">
              {t('assistant.empty.fallback.title')}
            </p>
            <p className="text-[12px] text-[var(--text-content-sub-title)]">
              {t('assistant.empty.fallback.body')}
            </p>
          </div>
          <TeeButton
            variant="secondary"
            size="s"
            disabled={disabled}
            onClick={onEscalate}
            text={t('assistant.empty.fallback.action')}
          />
        </div>
      )}

      {showRecent && (
        <div className="flex flex-col">
          <div className="flex items-center justify-between pb-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-content-sub-title)]">
              {t('assistant.empty.recent')}
            </p>
            {onViewAll && (
              <TeeButton
                variant="tertiary"
                size="s"
                onClick={onViewAll}
                text={t('assistant.empty.viewAll')}
              />
            )}
          </div>
          {recent.map((r) => (
            <div key={r.id} className="border-t border-[var(--border-default)]">
              <TeeButton
                variant="ghost"
                size="s"
                className="min-h-[44px] w-full justify-between"
                onClick={() => onSelectSession?.(r.id)}
                text={
                  <span className="flex w-full items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-[var(--text-content-title)]">
                        {r.title || t('assistant.panel.untitled')}
                      </span>
                      {r.tag && <span className={TAG_CLASS}>{t(`assistant.empty.tags.${r.tag}`)}</span>}
                    </span>
                    <span className="shrink-0 text-[12px] text-[var(--text-content-sub-title)]">
                      {relativeDate(r.lastMessageAt ?? r.updatedAt, i18n.language)}
                    </span>
                  </span>
                }
              />
            </div>
          ))}
        </div>
      )}

      {showCapabilities && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-content-sub-title)]">
              {t('assistant.empty.capabilities.title')}
            </p>
            {capabilities.map(([titleKey, bodyKey], i) => (
              <div key={titleKey} className="flex items-start gap-3">
                <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--color-gold-subtle)] font-[family-name:var(--type-font-family-heading)] text-[11px] font-bold text-[var(--color-gold-on-cream)]">
                  {i + 1}
                </span>
                <div className="flex flex-col gap-0.5">
                  <p className="text-[13px] font-semibold text-[var(--text-content-title)]">
                    {t(`assistant.empty.capabilities.${titleKey}`)}
                  </p>
                  <p className="text-[12px] text-[var(--text-content-sub-title)]">
                    {t(`assistant.empty.capabilities.${bodyKey}`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-[12px] text-[var(--text-content-sub-title)]">
            {t('assistant.empty.capabilities.caption')}
          </p>
        </div>
      )}
    </div>
  );
}
